import { useState } from "react";
import type { ProviderType } from "@racler/protocol";
import { encryptCredentialEnvelope } from "@racler/protocol";
import { api, errorText } from "../../lib/api.ts";

export const PROVIDER_OPTIONS: Array<{ id: ProviderType; label: string }> = [
  { id: "anthropic", label: "Anthropic" },
  { id: "gemini", label: "Google Gemini" },
  { id: "openai", label: "OpenAI" },
  { id: "openrouter", label: "OpenRouter" },
  { id: "custom", label: "Custom OpenAI-compatible" },
];

export function AddCredentialModal({
  machineId,
  machineName,
  onClose,
  onSuccess,
}: {
  machineId: string;
  machineName: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [provider, setProvider] = useState<ProviderType>("anthropic");
  const [label, setLabel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      setError("API Key is required");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Step 1: obtain machine's authenticated provisioning public key
      const keyRes = await api.getProvisioningKey(machineId);
      if (!keyRes.publicKeySpki) {
        throw new Error("Machine provisioning key is not available");
      }

      // Step 2: locally encrypt payload in browser
      const credentialId = crypto.randomUUID();
      const now = Date.now();
      const nonce = crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().replace(/-/g, "");

      const envelope = await encryptCredentialEnvelope(keyRes.publicKeySpki, {
        machineId,
        credentialId,
        provider,
        apiKey: apiKey.trim(),
        issuedAt: now,
        expiresAt: now + 300_000,
        nonce,
      });

      // Step 3: Clear API key input immediately
      setApiKey("");

      // Step 4: Relay ciphertext envelope through server
      await api.provisionCredential(machineId, {
        envelope,
        credentialId,
        provider,
        label: label.trim() || `${provider} (${new Date().toLocaleDateString()})`,
        baseUrl: baseUrl.trim() ? baseUrl.trim() : undefined,
      });

      onSuccess();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog-backdrop">
      <div className="dialog-card">
        <h3>Add Credential to {machineName}</h3>
        <p className="muted small">
          Your key will be encrypted in this browser using {machineName}'s provisioning key
          before sending. Plaintext never touches the server.
        </p>

        {error && <p className="error small">{error}</p>}

        <form onSubmit={(e) => { void handleSubmit(e); }}>
          <div className="form-row">
            <label>Provider</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value as ProviderType)}>
              {PROVIDER_OPTIONS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <label>Label</label>
            <input
              type="text"
              placeholder={`e.g. My ${provider} key`}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>

          {provider === "custom" && (
            <div className="form-row">
              <label>Base URL</label>
              <input
                type="url"
                placeholder="https://your-endpoint/v1"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                required
              />
            </div>
          )}

          <div className="form-row">
            <label>API Key</label>
            <input
              type="password"
              placeholder="sk-..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="btn-row">
            <button type="button" className="ghost-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="action-btn" disabled={saving}>
              {saving ? "Encrypting & Provisioning…" : "Encrypt & Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
